/**
 * chain-eos/prices.ts
 *
 * Fetches token USD prices from CoinGecko (free tier, no auth required).
 * Results are cached for 60 s to avoid hammering the API.
 */

export type TokenPrices = Record<string, number> // symbol → USD price

// CoinGecko coin IDs for our tracked tokens
const COINGECKO_IDS: Record<string, string> = {
  A: 'vaulta',
  EOS: 'eos',
  USDT: 'tether',
}

// Simple in-memory cache
let _cache: { prices: TokenPrices; ts: number } | null = null
const CACHE_TTL_MS = 60_000

// Stablecoins always equal $1 — no network call needed
const STABLE_USD: Record<string, number> = {
  USDT: 1,
  USDC: 1,
  DAI: 1,
}

export async function fetchTokenPrices(symbols?: string[]): Promise<TokenPrices> {
  // Return cached value if fresh
  if (_cache && Date.now() - _cache.ts < CACHE_TTL_MS) {
    return _cache.prices
  }

  // Symbols that actually need a CoinGecko call (exclude stables)
  const syms = (symbols ?? Object.keys(COINGECKO_IDS)).filter(s => !STABLE_USD[s])
  const ids = [...new Set(syms.map(s => COINGECKO_IDS[s]).filter(Boolean))]

  const prices: TokenPrices = { ...STABLE_USD }

  if (ids.length > 0) {
    try {
      const url = `https://api.coingecko.com/api/v3/simple/price?ids=${ids.join(',')}&vs_currencies=usd`
      const res = await fetch(url, { headers: { Accept: 'application/json' } })
      if (res.ok) {
        const data = (await res.json()) as Record<string, { usd?: number }>
        for (const [sym, cgId] of Object.entries(COINGECKO_IDS)) {
          if (!STABLE_USD[sym] && data[cgId]?.usd !== undefined) {
            prices[sym] = data[cgId].usd as number
          }
        }
      }
    } catch {
      // Network failure — merge stale cached non-stable prices
      if (_cache) {
        for (const [sym, price] of Object.entries(_cache.prices)) {
          if (!STABLE_USD[sym]) prices[sym] = price
        }
      }
    }
  }

  _cache = { prices, ts: Date.now() }
  return prices
}

/** Format a USD amount for display (e.g. 1956.77 → "$1,956.77", 0.004 → "$0.004") */
export function formatUsd(value: number): string {
  if (value === 0) return '$0'
  if (value < 0.01) return `$${value.toFixed(4)}`
  if (value < 1) return `$${value.toFixed(3)}`
  return value.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}
